import React, { useState } from 'react';
import { ChevronRight, ArrowLeft, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import VideoPlayer from '../components/VideoPlayer';
import ImageSlider from '../components/ImageSlider';

const SystemLibrary = () => {
  const logoSrc = '/logo.png';

  const [selectedSystem, setSelectedSystem] = useState('combined-forecast');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const systems = {
    institutional: [
      // Flagship Systems
      { id: 'hdalgo-pro-engine', name: 'Swing / Scalp Bot', tier: 'INSTITUTIONAL SUITE', flagship: true },
      { id: 'combined-forecast', name: 'Forecast Paint Engine', tier: 'INSTITUTIONAL SUITE', flagship: true },
      { id: 'trend-exhaustion', name: 'HDAlgos Trend Exhaustion Engine', tier: 'INSTITUTIONAL SUITE', flagship: true },
      { id: 'volatility-breakout', name: 'HDAlgos Volatility Breakout Engine', tier: 'INSTITUTIONAL SUITE', flagship: true },
      { id: 'sniper', name: 'HDAlgos Sniper', tier: 'INSTITUTIONAL SUITE', flagship: false },

      // Core Systems
      { id: 'emac-ml-adaptive', name: 'EMAC ML Adaptive System', tier: 'INSTITUTIONAL SUITE' },
      { id: 'cross-sectional', name: 'Cross-Sectional Relative Momentum', tier: 'INSTITUTIONAL SUITE' },
      { id: 'directional-probability', name: 'Directional Probability System', tier: 'INSTITUTIONAL SUITE' },
      { id: 'intraday-volatility', name: 'Intraday Volatility Timezone System', tier: 'INSTITUTIONAL SUITE' },

      // Advanced Systems
      { id: 'relative-trend', name: 'Relative Trend System', tier: 'INSTITUTIONAL SUITE' },
      { id: 'conditions-dashboard', name: 'Conditions Dashboard', tier: 'INSTITUTIONAL SUITE' },
      { id: 'auto-pattern-detection', name: 'Auto Pattern Detection', tier: 'INSTITUTIONAL SUITE' },

      // Elite Systems
      { id: 'regime-detection', name: 'Regime Detection Engine', tier: 'INSTITUTIONAL SUITE' },
      { id: 'ml-hybrid', name: 'ML Regime / Covariance Hybrid', tier: 'INSTITUTIONAL SUITE' },
      { id: 'pca-sensor', name: 'PCA Sensor', tier: 'INSTITUTIONAL SUITE' },
      { id: 'covariance-sensor', name: 'Covariance Sensor', tier: 'INSTITUTIONAL SUITE' },

      // Essential Systems
      { id: 'volatility-sensor', name: 'Volatility Sensor', tier: 'INSTITUTIONAL SUITE' },
      { id: 'cross-asset-trend', name: 'Cross Asset Trend', tier: 'INSTITUTIONAL SUITE' },
      { id: 'trend-exhaustion-lite', name: 'Trend Exhaustion Lite', tier: 'INSTITUTIONAL SUITE' },
      { id: 'trend-regime-lite', name: 'Trend Regime Lite', tier: 'INSTITUTIONAL SUITE' },
      { id: 'emac-forecast', name: 'EMAC Forecast System', tier: 'INSTITUTIONAL SUITE' },
      { id: 'volatility-trail-trend', name: 'Volatility Trail Trend Following', tier: 'INSTITUTIONAL SUITE' }
    ],
    custom: [
      { id: 'bespoke', name: 'Custom Indicator Build', tier: 'CUSTOM' }
    ]
  };

  const renderSystemLabel = (system) => (
    <div className="flex items-center justify-between gap-3 w-full">
      <span className="truncate">{system.name}</span>
      {system.flagship && (
        <span className="ml-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#CBB26A] bg-neutral-950/80 border border-[#CBB26A]/40 rounded-full px-2 py-0.5 whitespace-nowrap flex-shrink-0">
          Flagship
        </span>
      )}
    </div>
  );

  const systemDetails = {
    'hdalgo-pro-engine': {
      title: 'Swing / Scalp Bot',
      subtitle: 'Trade Maps, Not Numbers',
      icon: '',
      number: '0',
      tier: 'INSTITUTIONAL SUITE',
      images: ['/images/ESengine.png', '/images/pe1.png', '/images/engine1.png'],
      video: '/images/HDAlgosEngine-ES-vo.mp4',
      overview: 'HDAlgos Engine is a complete, clarity-driven trading suite that turns raw market noise into structured, actionable trade maps powered by a multi-factor, volatility-targeted forecast model that adapts instantly across markets and timeframes without revealing its proprietary logic. Signals are packaged into intuitive visual trade plans: automatic entries, projected multi-target exits, dynamic or trailing stops, clean pill-style labels, shaded risk-reward zones, and bar-strength colouring based on internal forecast conviction. Everything is designed to mirror how you actually trade, not how you wish you traded.',
      howItWorks: 'Backtesting realism is built in from the ground up — fixed/percentage/ATR stops, multi-target logic, trailing behaviour, break-even controls, session filters, non-repainting calculations, and intrabar-aware modelling ensure your stats reflect real execution rather than curve-fit fantasy. Two integrated performance tables tell you exactly what\'s working. The Trend & TF Map Panel shows your current position, trend state, ATR, and the directional alignment of every timeframe from 1-minute to Daily, so you instantly see whether a signal is supported or fighting higher-timeframe structure. The Performance & Risk Table turns your trading into measurable data: it breaks down trade counts, wins, and TP1/TP2/TP3 win rates for all trades, buys, and sells, then converts your capital and risk-settings into actual dollar and percentage outcomes. From the moment a signal fires, the Engine tracks whether each target was ever hit — TP1, TP2, TP3 — and converts those hits into Net R, Net % and Net $ results, showing which exit style would have grown your account over the chosen lookback period. You can adjust the lookback window, filter trades by session (e.g., 8am–2pm NY time), and analyse performance specifically for the hours you actually trade. Together, these tables act as your trading command-centre: one shows what the market is doing now, the other shows how your strategy has truly performed. Whether scalping, swinging, or investing, the Engine adapts automatically — reweighting forecast components and sensitivity to match your trading horizon — while the visuals remain clean, readable, and consistent. The result is simple: fewer impulsive trades, more high-quality opportunities, and a structured, repeatable trading process where your backtests, live metrics, and execution all speak the same language. Add HDAlgos Engine to your workflow and experience institutional-grade modelling translated into clarity, structure, and confidence on every chart. The Market Intelligence Panel adds a deeper layer of context by analysing the market\'s behavioural profile in real time. It displays a Conviction Score derived from the weighted forecast blend, the current Volatility Regime, and a Market Type classification based on MFE/MAE ratios (trend-favouring vs mean-reverting). It also evaluates the historically optimal exit (TP1/TP2/TP3), directional edge (buy vs sell expectancy), the best time-of-day window for signals, and excursion behaviour across recent trades. Cross-asset context is built in: the panel computes directional bias, trend strength and volatility state for major correlated instruments such as NQ, CL and BTC. A Synthetic Options Profile—Delta, Gamma, Theta and IV Proxy—is generated using price-based volatility and acceleration metrics to approximate how the environment "feels" from an options-risk perspective without relying on an options feed.',
      useCases: [
        'Choose your mode (Scalp / Swing / Invest) for automatic parameter optimization',
        'Wait for a clear regime flip with "Buy" or "Sell" label and shaded trade box',
        'Confirm the Current Trend and multi-TF map alignment',
        'Decide your exit profile using TP1 / TP2 / TP3 according to your style',
        'Monitor performance metrics to understand historical delivery and adjust TP preference based on data'
      ],
      interpretation: {
        example: 'Visual trade box displays: Entry: 21321.75 · Stop Loss: 21221.25 · Take Profit 1/2/3 with TP1 Win % 68%, TP2 Win % 45%, TP3 Win % 28%',
        meaning: 'The engine shows a structured trade setup with historical win rates for each target level, allowing data-driven decisions on profit-taking strategy'
      },
      tagline: 'A complete trading engine that answers three questions: What\'s the bias? Where\'s the trade? How has this actually performed?'
    },
    'combined-forecast': {
      title: 'Forecast Paint Engine',
      subtitle: 'A visual framework that blends seven institutional-grade forecasts into a single conviction-based paint system',
      icon: '',
      number: '1',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/paintmoney.png',
      overview: 'The Forecast Paint Engine distills our institutional research feed into a single, conviction-based paint layer. Instead of exposing raw model outputs, it communicates when conditions are supportive, neutral, or fading by translating internal readings into a clear visual story directly on the chart.',
      howItWorks: 'Every bar is graded by a proprietary ensemble that weighs directional bias, internal strength scores, and short-term context. Only when those readings align does the engine assign colour, borders, or highlight styles. Solid paints represent agreement between conviction and structure, border-only paints flag potential regime change awaiting confirmation, purple paints mark follow-through after a decisive event, and power bars are reserved for exceptionally strong readings detected by the framework. The underlying calculations remain sealed—what you see is the simplified, action-ready output.',
      tableGuide: {
        title: 'Table Guide (Right Panel)',
        rows: [
          { label: 'F1–F9', meaning: 'Individual forecast values (normalized ±20). Each measures different aspects of volatility, momentum, and structure.' },
          { label: 'Combined', meaning: 'Weighted composite forecast — the core engine reading.' },
          { label: 'State', meaning: 'The current market regime: bullish/bearish, strengthening/weakening.' },
          { label: 'IBS×', meaning: 'Intra-bar Strength multiple vs its rolling average. Shows if the bar\'s internal strength is statistically high.' },
          { label: 'CloseStr×', meaning: 'Closing-strength multiple vs average. Measures how decisively price closes near its extremes.' }
        ],
        note: 'Green "above" means the current bar\'s microstructure is strong; orange "below" means it\'s soft or neutral.'
      },
      barTypes: [
        { type: 'Lime (Solid)', state: 'Bullish Strengthening', meaning: 'Regime shift to bullish with strong IBS & CloseStr, and breakout of previous bar.' },
        { type: 'Soft Red (Solid)', state: 'Bearish Strengthening', meaning: 'Regime shift to bearish with strong IBS & CloseStr, and breakout below previous bar.' },
        { type: 'Orange (Solid)', state: 'Weakening', meaning: 'Transition phase — regime change detected but losing strength or internal conflict.' },
        { type: 'Dark Green / Dark Red', state: 'Power Bars', meaning: 'Statistically large bars (≥ 2× average range) with extreme IBS values. Represent powerful expansions.' },
        { type: 'White + Coloured Border', state: 'Early Regime Change', meaning: 'Forecast indicates regime change but bar lacks confirmation (IBS / CS below threshold).' },
        { type: 'Purple (Solid)', state: 'Follow-Through (Strong)', meaning: 'Continuation in the same direction, confirmed by strong bar structure.' },
        { type: 'White + Purple Border', state: 'Follow-Through (Weak)', meaning: 'Continuation in same direction but lacking strong IBS / CS confirmation.' }
      ],
      examples: [
        {
          title: 'Example 1',
          image: '/images/paintmain.png',
          description: 'The sequence begins with a solid red bar, signalling bearish strengthening. Because it\'s solid, it confirms strong intra-bar and closing strength, combined with a breakout below the previous bar — the start of a clear downside phase. Immediately after, a solid purple bar appears — a bearish follow-through. This means the trend continues in the same direction with confirmed internal bar strength, reinforcing short-side momentum. Next, an orange border candle forms — a bearish weakening state. The border indicates a regime change, but since the bar isn\'t filled, it lacks bar-level confirmation. This suggests that while forecasts detect a shift, internal strength is missing, hinting at a short-term reversal attempt. A red border bar follows, again showing forecast strength but without confirmation from the bar itself — a common sign of a potential pullback or pause within a continuing trend. Finally, another solid red bar appears, confirming renewed bearish strength and signalling the continuation of the move down.'
        },
        {
          title: 'Example 2',
          image: '/images/Screenshot 2025-11-06 at 17.01.18.png',
          description: 'A solid green bar marks bullish strengthening — strong IBS, strong closing strength, and a breakout above the previous bar. This bar represents a clear signal of conviction and directional alignment. It\'s followed by a solid purple bar, a bullish follow-through, confirming continuation in the same direction and reinforcing the strength of the breakout sequence. Later, a maroon Power Bar appears. This bar not only reflects bearish strengthening and strong structure, but because it\'s a Power Bar, it\'s also statistically large relative to recent history. It indicates a significant expansion in volatility and conviction — often the type of bar that defines short-term extremes or major directional pushes.'
        },
        {
          title: 'Example 3',
          image: '/images/paintBOFT1.png',
          description: 'The sequence opens with a dark green Power Bar, showing bullish strengthening and bar-level strength, along with a breakout above the prior bar. Its classification as a Power Bar means it\'s statistically large — a decisive move in both range and conviction. This is followed by a solid purple follow-through, confirming that the trend continues upward with the same structural strength and breakout characteristics. Together, this combination of a Power Bar and a strong follow-through indicates overextension — momentum that may have reached a short-term peak. Soon after, a solid red bar appears — bearish strengthening. It marks the forecast reversal, with strong bar structure and breakout confirmation to the downside, aligning with a shift toward renewed bearish control.'
        }
      ],
      useCases: [
        'Gauge the overall directional conviction of the market',
        'Adjust exposure dynamically (e.g., 50% position when forecast = +10, full position at +20)',
        'Identify regime shifts as forecasts transition between states',
        'Use as a top-level filter for discretionary or automated trading strategies'
      ],
      interpretation: {
        example: 'Forecast = +15, Bullish Strengthening',
        meaning: 'Suggests accelerating upward momentum; scale into longs, tighten short exposure'
      }
    },
    'trend-exhaustion': {
      title: 'HDAlgos Trend Exhaustion Engine',
      icon: '',
      number: '2',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/este.png',
      overview: 'The Trend Exhaustion System gauges how mature a directional move has become and communicates that maturity in plain language, giving traders an immediate sense of whether a run is fresh, aging, or nearing the point where momentum typically fades — all without exposing the mechanics behind the assessment.',
      howItWorks: 'Once a directional leg is recognised, the system references a deep historical library and tracks the move through proprietary aging bands. The display updates each bar with the active bias, age, and a clear exhaustion status so conviction and sizing can be tapered or pressed accordingly. All of the statistical modelling happens behind the scenes; the user-facing layer focuses purely on actionable context.',
      useCases: [
        'Exposure Management: Reduce position size as trends approach their historical median life',
        'Reversal Anticipation: Identify zones where the probability of continuation has dropped sharply before price reversal occurs',
        'Signal Combination: Pair with the Combined Forecast System — e.g., high forecast strength + young trend = high conviction; aged trend + decaying survival = reduced sizing',
        'Risk Control: Integrate with trailing-stop or scaling logic in systematic portfolios to manage exit timing objectively'
      ],
      interpretation: {
        example: 'Short trend, age 28 bars, survival probability 22%',
        meaning: 'The short move is statistically late-stage; continuation odds are low — consider scaling out, tightening stops, or preparing for a reversal'
      },
      tagline: 'Quantifies how much life is left in a trend — transforming intuition into measurable probability.'
    },
    'volatility-sensor': {
      title: 'Volatility Sensor',
      icon: '',
      number: '2',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/vol.png',
      overview: 'Detects volatility compression and expansion phases, signaling when to scale exposure or expect regime changes.',
      howItWorks: 'Monitors rolling realized volatility, ATR ratios, and volatility-of-volatility to categorize the market as stable, expanding, or contracting. Integrates directly with the combined forecast and regime models to dynamically modulate position sizing.',
      useCases: [
        'Reduce leverage during volatility expansion',
        'Identify breakouts from low-volatility compression zones',
        'Detect early warning signs of structural shifts in market behaviour'
      ],
      interpretation: {
        example: 'Volatility: Expanding rapidly',
        meaning: 'Indicates possible regime transition; tighten risk or wait for stability before adding exposure'
      }
    },
    'emac-forecast': {
      title: 'EMAC Forecast System',
      icon: '',
      number: '3',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/emac.png',
      overview: 'The EMAC Forecast (Exponential Moving Average Composite) measures trend acceleration and deceleration across multiple time constants.',
      howItWorks: 'Combines several EMAs into a composite signal that adapts to volatility and persistence. Unlike a standard MA crossover, EMAC identifies momentum acceleration rather than lagging confirmation.',
      useCases: [
        'Detect early stages of trend ignition',
        'Use in combination with the Combined Forecast for confirmation',
        'Filter short-term noise by weighting smoother long-term components'
      ],
      interpretation: {
        example: 'EMAC rising sharply',
        meaning: 'Acceleration phase; confirms strong directional momentum'
      }
    },
    'directional-probability': {
      title: 'Directional Probability System',
      icon: '',
      number: '4',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/prob.png',
      overview: 'Assigns a real-time probability of upward or downward continuation, turning historical distributions into forward-looking conviction estimates.',
      howItWorks: 'Analyzes rolling return distributions and volatility-adjusted price dynamics to compute the probability of the next bar closing higher or lower, conditioned on the current regime.',
      useCases: [
        'Quantify confidence before entry',
        'Filter trades — act only when directional probability exceeds a threshold (e.g., 65%)',
        'Blend with Trend Exhaustion to detect when probabilities diverge from momentum strength'
      ],
      interpretation: {
        example: 'Upward probability: 72%',
        meaning: 'Moderate conviction for continuation; may justify partial entry or scaling up'
      }
    },
    'cross-sectional': {
      title: 'Cross-Sectional Momentum / Relative Value System',
      icon: '',
      number: '5',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/crossmomentum.png',
      overview: 'The Cross-Sectional Momentum / Relative Value System measures and ranks assets against each other in real time to identify leaders (assets showing relative strength) and laggards (assets underperforming their cluster). It provides an institutional-style snapshot of relative momentum across a defined universe — highlighting where capital is flowing and where mean-reversion potential is building.',
      howItWorks: 'The system continuously evaluates a cluster of correlated assets — such as FX pairs, equity indices, or commodities — and computes each instrument\'s standardized forecast value (typically scaled between −20 and +20). This creates a dispersion map of momentum within the group: When dispersion expands (the gap between leaders and laggards widens), it signals a momentum regime: markets are trending and leadership is clear. When dispersion compresses, it indicates convergence and an increased likelihood of mean-reversion. Each asset is then tagged dynamically as Leader or Laggard, based on its position within the cluster\'s distribution. This ranking helps you visualise market structure — which assets are driving the move and which are trailing.',
      useCases: [
        'Rotational Strategies: Shift exposure toward top-ranked assets (leaders) while fading underperformers (laggards)',
        'Leadership Transitions: Detect when leadership flips (e.g., GBPUSD moving from laggard to leader), signalling rotation or regime change',
        'Portfolio Diversification: Balance exposures by ensuring allocation across uncorrelated or complementary clusters',
        'Confirmation Tool: Combine with regime or volatility systems to determine when relative momentum is statistically significant (momentum phase) or mean-reverting (consolidation phase)'
      ],
      interpretation: {
        example: 'Leader Assets: GBPCHF (+8.8), GBPUSD (+2.7) — Laggard Assets: EURUSD (−2.9), USDCAD (−3.6)',
        meaning: 'This configuration suggests GBP-linked strength relative to USD-linked weakness — a cross-pair rotation opportunity'
      },
      tagline: 'Ranks assets by strength and timing — revealing where leadership, rotation, and mean-reversion are statistically emerging.'
    },
    'regime-detection': {
      title: 'Regime Detection Engine',
      icon: '',
      number: '6',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/regime.png',
      additionalImages: ['/images/te1.png'],
      overview: 'Classifies the market into Trend or Chop environments using a machine-learning Hidden Markov Model.',
      howItWorks: 'Trained offline on historical returns, volatility, and persistence metrics for each asset and timeframe. Outputs the current regime, trend probability, and expected duration before regime transition.',
      useCases: [
        'Disable or reduce trend-following systems during choppy phases',
        'Increase conviction when regime probability confirms trending conditions',
        'Adapt portfolio risk and forecast scaling dynamically'
      ],
      interpretation: {
        example: 'Regime: Trend, Probability 82%',
        meaning: 'Continue trading with directional bias active. If Chop with 68% probability, reduce position size or tighten stops'
      }
    },
    'conditions-dashboard': {
      title: 'Conditions Dashboard',
      icon: '',
      number: '7',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/state.png',
      overview: 'The State Detection System acts as the framework\'s situational awareness layer — translating raw indicator outputs into cohesive market "states."',
      howItWorks: 'At its core, State Detection evaluates a structured matrix of technical and statistical indicators (e.g., EMAs, volatility, skewness, ADX, momentum accelerators). Each component feeds a binary or scaled state — such as Long, Short, Bull, Bear-Continue, or Contracting. Two synchronized tables form the visual interface: the Indicator State Table displays active sub-systems showing each indicator\'s current state, and the Timeframe Alignment Table aggregates EMAC and trend state readings across multiple timeframes.',
      useCases: [
        'Multi-timeframe confirmation: enter trades only when short- and medium-term states align',
        'Conflict detection: stand aside when lower- and higher-timeframe signals diverge',
        'Momentum monitoring: use the Forecast/Accel and Momentum State rows to identify strengthening vs. weakening phases',
        'Visual diagnostics: embed the table on live charts to instantly understand current structure',
        'Strategic scaling: combine with the Combined Forecast or Trend Exhaustion Systems'
      ],
      interpretation: {
        example: 'All timeframes: Bull-Continue',
        meaning: 'Strong aligned uptrend; maintain or scale into longs. Mixed states indicate transition phase; reduce exposure and monitor for volatility compression'
      }
    },
    'ml-hybrid': {
      title: 'MS-VAR Sensor (Markov-Switching Vector Autoregression System)',
      icon: '',
      number: '9',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/MSVAR1.png',
      overview: 'The MS-VAR Sensor maps dynamic leader–follower relationships between assets and detects how those connections evolve across market regimes. It combines Vector Autoregression (VAR) — which models how assets influence one another through time — with Markov-Switching state dynamics, allowing the relationships to shift between trend and chop conditions.',
      howItWorks: 'The system estimates a VAR model across a defined cluster of assets, quantifying how each asset\'s returns are influenced by lagged returns of others. Then, a two-state Markov-Switching process (Trend vs Chop) determines which regime the system is currently in and adjusts the relationship map accordingly. For each detected pair, the table displays: Leader / Follower (which asset statistically leads and which reacts), Lag (number of bars by which the follower tends to react), Regime (current dominant state), and Prob (probability that the pair is operating within that regime). These relationships are recalculated periodically and refitted on rolling windows, ensuring they adapt to structural changes in cross-asset behaviour.',
      useCases: [
        'Anticipatory Trading: Use leader signals to pre-empt moves in followers (e.g., ES1! leads NQ1! by 2 bars)',
        'Regime-Aware Correlation: Identify when normally correlated assets decouple under low-trend probability (chop regime)',
        'Cross-Market Confirmation: Validate directional bias by checking if leadership clusters align with Combined Forecast direction',
        'Signal Gating: In regime-filtered backtests, restrict trading to high-probability trend phases for improved Sharpe ratios'
      ],
      interpretation: {
        example: 'ES1! → NQ1! (Lag = 2, Regime = Trend, Prob = 82%) — CL1! → RB1! (Lag = 1, Regime = Trend, Prob = 77%)',
        meaning: 'Equity index leadership; follow ES1! for early momentum cues. Crude leading refined products; confirms energy cluster coherence.'
      },
      tagline: 'Learns which assets lead, which follow, and when those connections matter.'
    },
    'pca-sensor': {
      title: 'PCA Sensor (Principal Component Analysis Residual System)',
      icon: '',
      number: '10',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/pca1.png',
      overview: 'The PCA Sensor isolates the true underlying drivers of market movement by decomposing each asset\'s return into two parts — the factor (systemic, cluster-driven return) and the residual (asset-specific deviation). It\'s designed to detect when an asset\'s price diverges significantly from the behaviour of its correlated group — a signal that often precedes relative mean reversion or catch-up momentum.',
      howItWorks: 'Using Principal Component Analysis (PCA), the system identifies the dominant return factors within a defined cluster (e.g., equity indices, bonds, metals, FX). Each asset\'s daily return is then expressed as: Total Return = Factor + Residual. Factor is the portion of return explained by common market drivers (systemic behaviour). Residual is the idiosyncratic return unexplained by those shared factors — the "deviation." The system tracks both components as basis points and percentage share of the total return, giving a clear picture of how much of each asset\'s movement is market-driven versus unique. When residuals grow large and persistent — especially when mean-reversion conditions are flagged — the system highlights those assets as potential opportunities.',
      useCases: [
        'Relative-Value Trades: Identify assets whose residuals deviate significantly from their cluster factor (e.g., long laggards / short leaders)',
        'Cross-Asset Diagnostics: See which markets are currently factor-driven (macro regime dominant) vs. residual-driven (idiosyncratic dispersion)',
        'Volatility & Correlation Monitoring: Detect when residual volatility spikes, signalling reduced correlation within clusters',
        'Timing Mean Reversion: Combine with the Regime Detection Engine — trade reversion only during choppy or transitional regimes'
      ],
      interpretation: {
        example: 'ES1! — Factor: +9bp (7.8%), Residual: −107bp (92.2%) → USDJPY — Factor: +26bp (28.6%), Residual: +64bp (71.4%), Mean-Revert = Y',
        meaning: 'ES1! heavily residual-driven; potential for mean reversion if regime is choppy. USDJPY residual return dominant; watch for pullback or alignment with cluster.'
      },
      tagline: 'Quantifies when an asset is moving with the market — and when it\'s gone off on its own.'
    },
    'covariance-sensor': {
      title: 'Covariance Sensor',
      icon: '',
      number: '11',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/Picture_13.png',
      overview: 'Monitors real-time covariance and correlation structures across assets, forming the foundation for risk-based scaling.',
      howItWorks: 'Uses exponentially weighted covariance matrices to estimate how assets interact. Feeds directly into position sizing and cluster-based risk caps to prevent overexposure to correlated themes.',
      useCases: [
        'Manage cluster risk (e.g., avoid doubling exposure to ES and NQ)',
        'Dynamically cap exposure when correlations spike',
        'Integrate with ML hybrid to balance conviction vs. diversification'
      ],
      interpretation: {
        example: 'Equity cluster covariance rising sharply',
        meaning: 'Reduce combined notional in correlated longs'
      }
    },
    'cross-asset-trend': {
      title: 'Cross Asset Trend',
      icon: '',
      number: '10',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/xassettrend.png',
      tagline: 'Developed by a Former Goldman Sachs Trader',
      overview: 'The Cross-Asset Trend Dashboard offers a multi-market perspective — showing at a glance whether major assets (indices, bonds, commodities, FX, crypto) are trending bullish or bearish across multiple timeframes. It helps traders visualise global market alignment and identify when asset classes are moving together or diverging.',
      howItWorks: 'For each asset, the system classifies trend direction on several configurable timeframes (e.g., 60-min, 240-min, Daily). The colour-coded table updates dynamically: Bull = uptrend detected (green), Bear = downtrend detected (red). This provides a quick overview of market structure and trend synchrony across instruments.',
      useCases: [
        'Cross-market alignment: When most assets or timeframes show the same colour, global risk appetite is aligned',
        'Conflict detection: Mixed states highlight short-term versus long-term disagreement — useful for timing or hedging',
        'Sector rotation view: Spot when leadership shifts between clusters (e.g., equities turning while metals strengthen)'
      ],
      interpretation: {
        example: 'If Equity futures (ES1!, NQ1!) and Bonds (ZN1!, FGBL1!) all show Bear on short timeframes but Bull on the Daily',
        meaning: 'It suggests a short-term pullback inside a broader uptrend. When both short- and long-term columns turn the same colour, conviction in that direction increases'
      }
    },
    'trend-exhaustion-lite': {
      title: 'Trend Exhaustion Lite',
      icon: '',
      number: '11',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/trendlite.png',
      tagline: 'Developed by a Former Goldman Sachs Trader',
      overview: 'The Trend Exhaustion Lite tool tracks the age and strength of the current directional move to show how mature or extended a trend has become. It converts complex statistical analysis into a simple visual panel and colour-coded background.',
      howItWorks: 'Once a directional bias is established, the indicator measures: Direction (Bullish / Bearish phase), Trend Age (Number of bars since the current trend began), and Strength % (Relative intensity of the move on a 0–100 scale). Background colour shifts subtly as strength increases or fades, while the table displays live metrics.',
      useCases: [
        'Younger trends (low age): Usually early in development — potential continuation',
        'Aging trends (high age, declining strength): Often nearing exhaustion; tighten risk or prepare for rotation',
        'Combine with the Forecast Paint Engine or Trend Regime Lite for confirmation on when conviction is building or fading'
      ],
      interpretation: {
        example: 'Panel reads Direction: Bull, Age: 47 bars, Strength: 68%',
        meaning: 'The uptrend remains statistically healthy but entering a later stage — manage exposure accordingly'
      }
    },
    'trend-regime-lite': {
      title: 'Trend Regime Lite',
      icon: '',
      number: '12',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/regimelite.png',
      additionalImages: ['/images/trendlite1.png'],
      tagline: 'Developed by a Former Goldman Sachs Trader',
      overview: 'The Trend Regime Lite indicator identifies whether the market is currently in a Trending or Choppy state and estimates how long the current regime is expected to persist. It\'s a simplified, visual version of the full machine-learning regime model — focused on clarity and ease of interpretation.',
      howItWorks: 'Each bar is analysed to determine the most probable regime based on recent volatility and directional persistence. The lower-pane display shades the background according to the active regime: Trend (green) for directional phase, Chop (gray) for consolidation phase. The table summarises: State (Current regime classification), Probability (Confidence level of the active regime), and Expected Life (Approximate number of bars remaining before a likely transition).',
      useCases: [
        'Trade trend-following systems only when the regime = Trend and probability is high',
        'In Chop states, reduce exposure, tighten stops, or shift to mean-reversion tactics',
        'The "Expected Life" value helps gauge where the regime sits within its typical duration'
      ],
      interpretation: {
        example: 'State: Trend, Probability 87%, Expected Life 20 bars',
        meaning: 'Price behaviour currently supports continuation, but the regime may shift if volatility contracts'
      }
    },
    'emac-ml-adaptive': {
      title: 'EMAC ML Adaptive System',
      icon: '',
      number: '13',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/emac.png',
      comingSoon: true,
      overview: 'Machine learning enhanced version of EMAC Forecast with adaptive parameters that optimize to current market conditions.',
      howItWorks: 'Uses online learning algorithms to continuously adapt EMA weights and lookback periods based on realized performance and regime characteristics.',
      useCases: [
        'Adaptive trend detection with self-optimizing parameters',
        'Improved momentum identification in changing volatility regimes',
        'Dynamic signal generation that evolves with market structure'
      ],
      interpretation: {
        example: 'EMAC ML: Strong upward, Confidence 85%',
        meaning: 'High-confidence momentum acceleration detected by adaptive model'
      }
    },
    'intraday-volatility': {
      title: 'Intraday Volatility Timezone System',
      icon: '',
      number: '14',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/intradayvol.png',
      additionalImages: ['/images/intradayvol1.png'],
      tagline: 'Developed by a Former Goldman Sachs Trader',
      overview: 'The Intraday Volatility System quantifies and visualises how volatility evolves across global trading sessions — highlighting when market activity deviates from its typical rhythm. Instead of relying on guesswork, it breaks down volatility into session-based components (Asia, London, New York) and compares current conditions to historical norms, making it easier to understand when and where excess movement occurs.',
      howItWorks: 'The system continuously measures realised price movement within each major session and scales it relative to that session\'s own historical average. It displays a session-summary dashboard with metrics: Session (Global trading period), vol (×1e4) (Standardised measure of realised volatility magnitude), Z-Score (How current volatility compares to long-term average), State (Qualitative label: Normal, High, Low), and Excess x (Multiplier showing how many times current volatility exceeds or falls short of baseline). The shaded area in the lower pane visually represents intraday volatility intensity — expanding during active phases and contracting when markets are quiet.',
      useCases: [
        'Session Comparison: Identify which region is currently driving price movement. A spike in Asia or London session volatility may hint at macro catalysts or flow shifts',
        'Volatility Timing: Monitor how volatility rotates between sessions — useful for planning trade timing or stop-width adjustments',
        'Regime Awareness: High-vol regimes suggest more opportunity but also greater drawdown risk; low-vol regimes imply compression or waiting phases',
        'Excess Monitoring: Use the "Excess x" value to see whether the market is behaving abnormally relative to its usual session dynamics'
      ],
      interpretation: {
        example: 'Overall volatility state reads High, London and New York remain Normal',
        meaning: 'The spike may have been isolated to one region (e.g., an event in Asia). Such insight helps traders quickly determine where volatility is emerging and whether it is contained or spreading across sessions'
      }
    },
    'volatility-breakout': {
      title: 'HDAlgos Volatility Breakout Engine',
      icon: '',
      number: '15',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/esbo.png',
      additionalImages: ['/images/VBO.png'],
      tagline: 'Developed by a Former Goldman Sachs Trader',
      overview: 'The Volatility Breakout Engine keeps a running read on how price is interacting with nearby structure under the prevailing volatility and volume backdrop. It spots compression releases, retests, and normalised phases so traders understand whether energy is building or decaying without revealing the proprietary calculations that power those reads.',
      howItWorks: 'Recent price action, volatility tone, volume posture, and proximity to important zones are all screened through internal thresholds. The dashboard then surfaces the outcome as intuitive labels like core breakout status, filtered confirmation, regime context, and time since the last event. By sharing only the contextual summary while obscuring the underlying maths, the system delivers clarity without disclosing the mechanics.',
      useCases: [
        'Context, not signals: Use the table to understand where price is relative to volatility expansion and prior structure — not as a trigger',
        'Fresh Breakouts: "Fresh breakout" status marks newly established directional moves; these often define early trend phases',
        'Weakening Levels: When breakout status reads "3rd + test (weakening)," it suggests repeated interaction with the same zone — structure may be losing integrity',
        'High Volatility Regimes: Breakouts during "High vol" regimes often move further but also retrace faster — size risk accordingly',
        'Support/Resistance Context: S/R scale near 0 → price near key level; near 1 → far from structure'
      ],
      interpretation: {
        example: 'Bearish N-bar Breakout under High volatility regime, Fresh breakout status',
        meaning: 'A new high-energy move has begun, with volatility expansion supporting direction. Monitor for follow-through or false break reversal'
      }
    },
    'sniper': {
      title: 'HDAlgos Sniper',
      icon: '',
      number: '19',
      tier: 'INSTITUTIONAL SUITE',
      images: ['/images/custom-strategy.png'],
      overview: 'A complete, configurable backtesting strategy for TradingView that unifies entries, exits, scaling and risk into one automation-ready package. Built for lower timeframes and futures-style scalping, but adaptable to any liquid market. Sniper mirrors live execution with realistic logic (multi-targets, BE moves, trails, session filters) so your stats match how you actually trade.',
      useCases: [
        'Low-timeframe futures scalping with precise confluence and risk',
        'Session-aware backtesting with realistic stop/target/trailing logic',
        'Webhook-friendly outputs for third-party automation'
      ],
      interpretation: {
        example: 'Scalp mode + ATR stops with quick TP1/TP2, BE shift on momentum stall',
        meaning: 'Focus on fast breakout/impulse legs while controlling drawdown through intrabar-aware trailing and session gates'
      }
    },
    'auto-pattern-detection': {
      title: 'Auto Pattern Detection',
      icon: '',
      number: '20',
      tier: 'INSTITUTIONAL SUITE',
      images: ['/images/patterndetec.png'],
      overview: 'Automatically detects high-probability price structures (wedges, flags, triangles, double tops/bottoms and more) and paints clean execution context directly on chart. Designed to complement the Forecast Paint and Engine outputs with structural confluence.',
      useCases: [
        'Identify continuation (flags) vs. exhaustion (wedges) in real time',
        'Add structure-based confirmation to breakout and trend entries',
        'Scan across assets/timeframes to spot emerging setups'
      ],
      interpretation: {
        example: 'Bull flag detected within trend-aligned environment',
        meaning: 'Pattern confirms continuation bias; use Engine targets and ATR stops to frame the trade'
      }
    },
    'relative-trend': {
      title: 'Relative Trend System',
      icon: '',
      number: '16',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/reltrendvol.png',
      additionalImages: ['/images/reltrendvoltable.png'],
      tagline: 'Developed by a Former Goldman Sachs Trader',
      overview: 'The Relative Trend System compares multiple assets side-by-side to identify which markets are leading or lagging in trend strength and volatility. Rather than focusing on absolute direction, it measures each asset\'s relative momentum and relative volatility within its own cluster (e.g., Equities, Rates, Commodities). The output table provides a snapshot of cross-asset behaviour — showing trend alignment, divergence, and sector leadership — helping traders visualise market rotation in real time.',
      howItWorks: 'Each asset\'s recent trend and volatility readings are standardised as Z-scores (statistical deviations from their historical mean). This allows fair comparison between different instruments, even when they trade in different ranges or currencies. The system then ranks assets by trend strength (Rank T) and calculates Rel Trend Z (Standardised measure of relative trend momentum) and Rel Vol Z (Standardised measure of relative volatility). Below the table, aggregate statistics summarise overall trend dispersion (Trend σ), volatility dispersion (Vol σ), and market breadth (percentage of assets with statistically strong trends).',
      useCases: [
        'Identify leaders and laggards: Assets with high positive Rel Trend Z are outperforming within their cluster; negative readings indicate laggards',
        'Monitor cross-sector rotation: Shifts in cluster leadership often precede regime transitions (e.g., rotation from Equities to Metals)',
        'Gauge environment strength: Low Trend σ implies uniform direction; high Trend σ signals mixed conditions and weaker conviction',
        'Volatility context: Assets with high Rel Vol Z tend to exhibit faster rotation and larger swings — suitable for tactical trading'
      ],
      interpretation: {
        example: 'GC1! (Gold) shows High-T / High-V, CL1! (Crude Oil) shows Weak-T / High-V',
        meaning: 'This indicates leadership in Metals and weakness in Energy under current market dispersion. The pair idea (Long GC1! / Short CL1!) illustrates cross-asset divergence — purely statistical, not a trade recommendation'
      },
      disclaimer: 'Any "Pair Idea" displayed is purely statistical and illustrative, highlighting observed divergence between instruments. It does not constitute financial advice or a trade recommendation. Trading involves risk; always perform your own due diligence before taking positions.'
    },
    'volatility-trail-trend': {
      title: 'Volatility Trail Trend Following',
      icon: '',
      number: '17',
      tier: 'INSTITUTIONAL SUITE',
      image: '/images/Volatility Trail Trend Following.png',
      overview: 'The Trend Following Volatility Trail is a dynamic trend-following tool that adapts its stop, bias, and zones to real-time volatility and trend strength. Instead of using static ATR multiples like a normal Supertrend or Chandelier Stop, it continuously adjusts itself based on how stretched the market is and how persistent the trend has been. This makes the system far more reactive during momentum phases and more conservative during consolidation, helping avoid fake flips and late entries.',
      howItWorks: 'The indicator builds an adaptive trail around a smoothed price basis: It starts with a short EMA as the "core trend line." It measures volatility expansion versus normal volatility. It measures trend persistence by reading whether price has been rising or falling consistently. These two components combine to adjust the ATR multiplier dynamically. As volatility expands or the trend becomes more persistent, the bands widen. When volatility compresses or the trend weakens, the bands tighten. These adaptive bands form the foundation of the trailing system. The tool constantly tracks whether price is above or below the adaptive trail: Price above the upper trail → Bullish regime, Price below the lower trail → Bearish regime. But instead of flipping immediately, it waits for confirmation bars to avoid noise. This greatly reduces whipsaws and keeps the focus on sustained moves. Once a new regime is confirmed: A coloured cloud appears (bull or bear), A label marks the flip point, Alerts can be triggered automatically.',
      useCases: [
        'Identifying regime shifts early',
        'Riding sustained trends with confidence',
        'Avoiding choppy markets by requiring confirmation',
        'Using the adaptive cloud as a directional bias layer'
      ],
      interpretation: {
        example: 'Price breaks above adaptive trail with confirmation → Bullish regime confirmed',
        meaning: 'Enter or maintain long positions, as the system has validated a sustained upward move with reduced whipsaw risk'
      }
    },
    'bespoke': {
      title: 'Custom Indicator Build',
      icon: '',
      number: '18',
      tier: 'CUSTOM',
      image: '/images/curves.png',
      overview: 'Everything in the Institutional Suite, plus a fully custom indicator built personally by a Former Goldman Sachs Trader. We build your vision — tailored to your exact specifications, volatility budgets, risk preferences, and strategic goals.',
      howItWorks: 'Through one-on-one consultation sessions, our Former Goldman Sachs Trader works with you to understand your trading style and objectives. The custom indicator architecture, weighting logic, and model configurations are designed specifically around your needs and asset universe. Includes ongoing support and refinements. Delivered with comprehensive research documentation detailing development, validation, and results.',
      useCases: [
        'Fund or desk-specific systemization',
        'Bespoke volatility targeting and cluster management',
        'Integrating proprietary data or execution layers into the architecture'
      ],
      interpretation: {
        example: 'Custom Forecast Blend (Energy-focused, 4h data) → Sharpe 2.8 post-vol-scaling',
        meaning: 'Tailored system achieving superior risk-adjusted returns in specific market segment'
      }
    }
  };

  const current = systemDetails[selectedSystem];

  const getTierColor = (tier) => {
    switch (tier) {
      case 'INSTITUTIONAL SUITE': return 'text-[#CBB26A]';
      case 'CUSTOM': return 'text-[#CBB26A]';
      default: return 'text-neutral-400';
    }
  };

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#111315]/95 backdrop-blur-sm border-b border-neutral-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition">
              <ArrowLeft className="w-5 h-5 text-[#CBB26A]" />
              <div className="flex items-center">
                <img src={logoSrc} alt="HDAlgos logo" className="h-10 w-auto" />
                <span className="sr-only">HDAlgos System Library</span>
              </div>
            </Link>

            {/* Prompt - visible on all screen sizes */}
            <div className="flex items-center gap-2 text-sm text-neutral-400">
              <ChevronRight className="w-4 h-4 text-[#CBB26A]" />
              <span className="hidden sm:inline">Browse all systems in the sidebar</span>
              <span className="sm:hidden">More systems below</span>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-neutral-800 rounded-lg transition"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-20 flex">
        {/* Mobile Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-[#111315]/70 z-30 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`w-80 fixed left-0 h-screen bg-neutral-950 border-r border-neutral-900 overflow-y-auto transition-transform duration-300 z-40 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
          <div className="p-6 space-y-8">
            {/* INSTITUTIONAL SUITE */}
            <div>
              <div className="text-xs font-bold text-[#CBB26A] mb-3 tracking-wider">INSTITUTIONAL SUITE</div>
              <div className="space-y-1">
                {systems.institutional.map((system) => (
                  <button
                    key={system.id}
                    onClick={() => {
                      setSelectedSystem(system.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition ${
                      selectedSystem === system.id
                        ? 'bg-[#CBB26A] text-black font-bold'
                        : 'text-neutral-400 hover:bg-neutral-900 hover:text-white'
                    }`}
                  >
                    {renderSystemLabel(system)}
                  </button>
                ))}
              </div>
            </div>

            {/* CUSTOM INDICATOR */}
            <div>
              <div className="text-xs font-bold text-[#CBB26A] mb-3 tracking-wider">CUSTOM INDICATOR</div>
              <div className="space-y-1">
                {systems.custom.map((system) => (
                  <button
                    key={system.id}
                    onClick={() => {
                      setSelectedSystem(system.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition ${
                      selectedSystem === system.id
                        ? 'bg-[#CBB26A] text-black font-bold'
                        : 'text-neutral-400 hover:bg-neutral-900 hover:text-white'
                    }`}
                  >
                    {renderSystemLabel(system)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:ml-80 flex-1 p-6 md:p-12">
          <div className="max-w-5xl mx-auto">
            <div key={selectedSystem}>
              {/* Header */}
              <div className="mb-12">
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-6xl">{current.icon}</span>
                  <div>
                    <div className={`text-sm font-bold ${getTierColor(current.tier)} tracking-wider mb-2`}>
                      {current.tier} TIER • SYSTEM #{current.number}
                    </div>
                    <h1 className="text-5xl font-black">{current.title}</h1>
                  </div>
                </div>
              </div>

              {/* Video (if available) */}
              {current.video && (
                <div className="mb-12">
                  <VideoPlayer
                    src={current.video}
                    className="w-full rounded-xl border-2 border-[#CBB26A]/30 shadow-2xl shadow-[#CBB26A]/10"
                    autoPlay={false}
                    enableMobileExpand={true}
                  />
                </div>
              )}

              {/* Image(s) - Use slider if images array exists, otherwise fallback to single image */}
              <div className="mb-12">
                {current.images ? (
                  <ImageSlider images={current.images} alt={current.title} />
                ) : current.image ? (
                  <img
                    src={current.image}
                    alt={current.title}
                    className="w-full rounded-xl border-2 border-[#CBB26A]/30 shadow-2xl shadow-[#CBB26A]/10"
                    loading="lazy"
                  />
                ) : null}
                {!current.images && current.additionalImages && current.additionalImages.length > 0 && (
                  <div className="mt-6 grid grid-cols-1 gap-6">
                    {current.additionalImages.map((imgSrc, idx) => (
                      <img
                        key={idx}
                        src={imgSrc}
                        alt={`${current.title} - View ${idx + 2}`}
                        className="w-full rounded-xl border-2 border-[#CBB26A]/30 shadow-2xl shadow-[#CBB26A]/10"
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}
              </div>

              {current.comingSoon ? (
                <div className="mb-12 bg-neutral-900 border-2 border-dashed border-[#CBB26A]/60 p-10 rounded-2xl text-center">
                  <p className="text-sm font-bold text-[#CBB26A] uppercase tracking-[0.6em] mb-4">
                    Coming Soon
                  </p>
                  <p className="text-xl text-neutral-300 leading-relaxed max-w-2xl mx-auto">
                    Full documentation, visuals, and implementation details for this system are being finalised.
                    Check back shortly for the complete breakdown.
                  </p>
                </div>
              ) : (
                <>
                  {/* Overview */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-[#CBB26A]">Overview</h2>
                    <p className="text-xl text-neutral-300 leading-relaxed">{current.overview}</p>
                  </div>

                  {/* How It Works */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-[#CBB26A]">How It Works</h2>
                    <p className="text-lg text-neutral-400 leading-relaxed">{current.howItWorks}</p>
                  </div>

                  {/* Use Cases */}
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-[#CBB26A]">Use Cases</h2>
                    <ul className="space-y-3">
                      {current.useCases.map((useCase, index) => (
                        <li key={index} className="flex items-start gap-3 text-neutral-300">
                          <ChevronRight className="w-5 h-5 text-[#CBB26A] flex-shrink-0 mt-1" />
                          <span className="text-lg leading-relaxed">{useCase}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Table Guide (for Forecast Paint Engine) */}
                  {current.tableGuide && (
                    <div className="mb-8">
                      <h2 className="text-2xl font-bold mb-4 text-[#CBB26A]">{current.tableGuide.title}</h2>
                      <div className="bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-neutral-800">
                              <th className="text-left p-4 text-[#CBB26A] font-bold">Row</th>
                              <th className="text-left p-4 text-[#CBB26A] font-bold">Meaning</th>
                            </tr>
                          </thead>
                          <tbody>
                            {current.tableGuide.rows.map((row, index) => (
                              <tr key={index} className="border-t border-neutral-800">
                                <td className="p-4 text-neutral-300 font-semibold">{row.label}</td>
                                <td className="p-4 text-neutral-400">{row.meaning}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {current.tableGuide.note && (
                        <p className="text-sm text-neutral-500 mt-3 italic">{current.tableGuide.note}</p>
                      )}
                    </div>
                  )}

                  {/* Bar Types Reference (for Forecast Paint Engine) */}
                  {current.barTypes && (
                    <div className="mb-8">
                      <h2 className="text-2xl font-bold mb-4 text-[#CBB26A]">Bar Type Reference</h2>
                      <div className="bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-neutral-800">
                              <th className="text-left p-4 text-[#CBB26A] font-bold">Bar Type</th>
                              <th className="text-left p-4 text-[#CBB26A] font-bold">State</th>
                              <th className="text-left p-4 text-[#CBB26A] font-bold">Meaning</th>
                            </tr>
                          </thead>
                          <tbody>
                            {current.barTypes.map((bar, index) => (
                              <tr key={index} className="border-t border-neutral-800">
                                <td className="p-4 text-neutral-300 font-semibold">{bar.type}</td>
                                <td className="p-4 text-neutral-300">{bar.state}</td>
                                <td className="p-4 text-neutral-400">{bar.meaning}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Examples (for Forecast Paint Engine) */}
                  {current.examples && (
                    <div className="mb-8">
                      <h2 className="text-2xl font-bold mb-6 text-[#CBB26A]">Examples</h2>
                      <div className="space-y-8">
                        {current.examples.map((example, index) => (
                          <div key={index} className="bg-neutral-900 border border-neutral-800 rounded-lg p-6">
                            <h3 className="text-xl font-bold mb-4 text-[#CBB26A]">{example.title}</h3>
                            <div className="mb-4">
                              <img
                                src={example.image}
                                alt={example.title}
                                className="w-full h-auto rounded-lg border border-neutral-700"
                              />
                            </div>
                            <p className="text-lg text-neutral-300 leading-relaxed">{example.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interpretation Example */}
                  <div className="bg-neutral-900 border-2 border-[#CBB26A]/30 p-8 rounded-xl mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-[#CBB26A]">Interpretation Example</h2>
                    <div className="mb-4">
                      <div className="text-sm text-neutral-500 mb-2">Example Output:</div>
                      <div className="text-xl font-mono text-white bg-[#111315] p-4 rounded border border-neutral-800">
                        {current.interpretation.example}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-neutral-500 mb-2">Meaning:</div>
                      <p className="text-lg text-neutral-300 leading-relaxed">{current.interpretation.meaning}</p>
                    </div>
                  </div>

                  {/* Tagline */}
                  {current.tagline && (
                    <div className="bg-[#CBB26A]/20 border-2 border-[#CBB26A]/30 p-6 rounded-xl text-center">
                      <p className="text-xl text-[#CBB26A] font-semibold italic leading-relaxed">
                        "{current.tagline}"
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemLibrary;
